import React from 'react';
import { motion } from 'framer-motion';
import { Building2Icon, Globe2Icon, MapPinIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const COMPANIES = [{
  name: 'GSK',
  logo: 'https://cdn.magicpatterns.com/uploads/aCqnqXncNjk1xsdUQg5TFB/Screenshot_2026-08-20_at_21.22.24.png'
}, {
  name: 'LEO Pharma',
  logo: 'https://cdn.magicpatterns.com/uploads/qe6bAu8fK165LTd9pa9Tmm/Screenshot_2026-08-20_at_21.22.35.png'
}, {
  name: '3M and Solventum',
  logo: 'https://cdn.magicpatterns.com/uploads/8a94AChcQKnfAzckma8QZD/Screenshot_2026-08-27_at_12.40.13.png'
}, {
  name: 'AbbVie',
  logo: 'https://cdn.magicpatterns.com/uploads/pKJuSjA5sXAAftL8qfcZvo/Screenshot_2026-08-27_at_12.39.43.png'
}, {
  name: 'Roche',
  logo: 'https://cdn.magicpatterns.com/uploads/aQbK55PqMKmQ6yF9dTcsFH/Screenshot_2026-08-20_at_21.22.20.png'
}] as const;
const REGIONS = ['Europe', 'Gulf', 'Africa', 'Asia', 'South America'] as const;
const MARKETS = ['Finland', 'Denmark', 'Germany', 'Poland', 'France', 'Netherlands', 'United Kingdom', 'Spain', 'Italy', 'Gulf', 'Morocco', 'Ivory Coast', 'Nigeria', 'South Africa', 'Kenya', 'Singapore', 'China', 'Peru', 'Brazil', 'Ireland', 'Sweden', 'Romania', 'Hungary'] as const;
export function SlideOperatingScale({}: SlideProps) {
  return <InteriorSlide surface data-id="element-330" data-mp="1tf3ikz1xzj8">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.55
    }} className="mb-[clamp(1.5rem,2.4vw,2.5rem)] flex-shrink-0" data-id="element-331" data-mp="1sn7j91jlzsf">
        <p className="mb-3 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-332" data-mp="auwdrmps3700">
          Global delivery experience
        </p>
        <h2 className="max-w-6xl text-[clamp(2rem,3.8vw,3.6rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-333" data-mp="1m8qrvy51c4d3">
          Built for continued campaigns—
          <span className="text-[var(--brand)]" data-id="element-334" data-mp="1ia19fr553wml">already operating at scale.</span>
        </h2>
        <p className="mt-4 max-w-3xl text-[clamp(0.95rem,1.25vw,1.15rem)] leading-relaxed text-[var(--muted)]" data-id="element-335" data-mp="ez56ladb08f0">
          Continued campaign delivery is already being managed for leading
          healthcare companies across a broad international footprint.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-12" data-id="element-336" data-mp="sufxkcofg9e3">
        <motion.article initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.18,
        duration: 0.55
      }} className="flex min-h-0 flex-col rounded-3xl border border-black/5 bg-white p-[clamp(1.25rem,2vw,2rem)] shadow-sm lg:col-span-4" data-id="element-337" data-mp="3sd8le8onp0lw">
          <div className="flex items-start justify-between gap-4" data-id="element-338" data-mp="9ziwvawopaay">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-339" data-mp="39ko5rsxyws01">
              <Building2Icon className="h-5 w-5" aria-hidden="true" data-id="element-340" data-mp="3dkes9kggolyr" />
            </div>
            <span className="text-[clamp(2.5rem,4vw,4rem)] font-extrabold leading-none text-[var(--brand)]" data-id="element-341" data-mp="3615uypl8cdq2">
              5
            </span>
          </div>
          <h3 className="mt-4 text-xl font-bold text-[var(--fg)]" data-id="element-342" data-mp="2x1irzzcuqzuw">
            Companies with managed campaigns
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-[var(--muted)]" data-id="element-343" data-mp="2s2z8e5lqe05p">
            Selected organizations supported through continued campaign delivery.
          </p>
          <div className="mt-5 grid flex-1 grid-cols-2 gap-3" data-id="element-344" data-mp="2icmngca084z1">
            {COMPANIES.map((company, index) => <figure key={company.name} className={`flex min-h-[4rem] items-center justify-center overflow-hidden rounded-2xl border border-[var(--brand)]/10 bg-white px-4 py-2 ${index === COMPANIES.length - 1 ? 'col-span-2' : ''}`} data-id="element-345" data-mp="2dfmg7y1f4w6c" data-mpi={index}>
                <img src={company.logo} alt={`${company.name} logo`} className="h-full max-h-14 w-full object-contain" data-id="element-346" data-mp="24d4wbq98kfmi" />
              </figure>)}
          </div>
        </motion.article>

        <motion.article initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3,
        duration: 0.55
      }} className="relative flex min-h-0 flex-col justify-between overflow-hidden rounded-3xl bg-[var(--brand)] p-[clamp(1.4rem,2.2vw,2.25rem)] text-white shadow-lg lg:col-span-3" data-id="element-347" data-mp="8b1e09jl0brk">
          <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full border-[28px] border-white/10" data-id="element-348" data-mp="3ks6qdbndr4p9" />
          <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15" data-id="element-349" data-mp="36snimjyhdvdh">
            <div className="h-6 w-6" aria-hidden="true" data-id="element-350" data-mp="3bqyqwq94nxtl" />
          </div>
          <div className="relative" data-id="element-351" data-mp="3btbh1qp1hehq">
            <p className="text-[clamp(4.5rem,8vw,7.5rem)] font-extrabold leading-none tracking-[-0.06em]" data-id="element-352" data-mp="150r5x70kufh4">
              22
            </p>
            <h3 className="mt-3 text-[clamp(1.25rem,2vw,1.75rem)] font-bold" data-id="element-353" data-mp="19zc6x8nnl36m">
              Brands campaigned
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-white/75" data-id="element-354" data-mp="2pj3450uk1hfy">
              Across different portfolios, lifecycle stages, and HCP needs.
            </p>
            <p className="mt-5 border-t border-white/20 pt-4 text-xs font-semibold uppercase tracking-[0.14em] text-white/65" data-id="element-355" data-mp="349ioyej26ndl">
              Continued delivery experience
            </p>
          </div>
        </motion.article>

        <motion.article initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.42,
        duration: 0.55
      }} className="flex min-h-0 flex-col rounded-3xl border border-[var(--brand)]/10 bg-white p-[clamp(1.25rem,2vw,2rem)] shadow-sm lg:col-span-5" data-id="element-356" data-mp="3dagxna4fzj2i">
          <div className="flex items-start justify-between gap-4" data-id="element-357" data-mp="1ss0vc7v6z44s">
            <div className="flex items-center gap-3" data-id="element-358" data-mp="1ntpl3w0zirm1">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-359" data-mp="1nv0wagwrf1n6">
                <Globe2Icon className="h-5 w-5" aria-hidden="true" data-id="element-360" data-mp="2dk7r5v2y5qoh" />
              </span>
              <div data-id="element-361" data-mp="2hlj7tb3uf1kj">
                <p className="text-[clamp(2rem,3.2vw,3rem)] font-extrabold leading-none text-[var(--fg)]" data-id="element-362" data-mp="2mijdhkws62b4">
                  5
                </p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-[var(--muted)]" data-id="element-363" data-mp="2rifj1ol4m1ss">
                  Regions covered
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-364" data-mp="30h9cx772oe2n">
              <p className="text-[clamp(2rem,3.2vw,3rem)] font-extrabold leading-none text-[var(--brand)]" data-id="element-365" data-mp="30g7ycsqul9g6">
                23
              </p>
              <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-[var(--muted)]" data-id="element-366" data-mp="3a8dpk099zmub">
                Localizations
              </p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2" data-id="element-367" data-mp="3k61vlmcc0grs">
            {REGIONS.map(region => <span key={region} className="rounded-full border border-[var(--brand)]/15 bg-[var(--brand)]/5 px-3 py-1.5 text-xs font-bold text-[var(--brand)]" data-id="element-368" data-mp="3k3xiqewgg7fd">
                {region}
              </span>)}
          </div>

          <div className="mt-5 flex min-h-0 flex-1 flex-col rounded-2xl bg-[var(--surface)] p-4" data-id="element-369" data-mp="ykiqikvsf0wt">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-[var(--fg)]" data-id="element-370" data-mp="tlxocfwz675q">
              <MapPinIcon className="h-4 w-4 text-[var(--brand)]" aria-hidden="true" data-id="element-371" data-mp="1r96ckiod1ubf" />
              Market footprint
            </div>
            <ul className="grid flex-1 grid-cols-3 gap-x-4 gap-y-1.5" data-id="element-372" data-mp="1mbo1omqs950s">
              {MARKETS.map(market => <li key={market} className="flex items-center gap-1.5 text-[0.68rem] font-medium leading-tight text-[var(--muted)]" data-id="element-373" data-mp="1hil7qdi3ftk2">
                  <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--brand)]" data-id="element-374" data-mp="1cih0le32sb6i" />
                  {market}
                </li>)}
            </ul>
          </div>
        </motion.article>
      </div>
    </InteriorSlide>;
}